import React from 'react';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Button } from '@progress/kendo-react-buttons';
import { CpsGridView } from '@cs-cms/types';
import { findViewById, getSortedViews } from './userViewsUtils';

const renderViewDropdownItem = (li: React.ReactElement, itemProps: { dataItem: CpsGridView }): React.ReactElement => {
  const { dataItem } = itemProps;
  const isSystemView = !dataItem.isPrivate;

  return React.cloneElement(
    li,
    li.props as React.HTMLAttributes<HTMLElement>,
    <span style={{ fontWeight: isSystemView ? '600' : 'normal' }}>{dataItem.name}</span>
  );
};

interface UserViewsToolbarProps {
  views: CpsGridView[];
  selectedView: number | null | undefined;
  translate: (key: string, params?: Record<string, string>) => string;
  isPrivateViewLimitReached: boolean;
  isRenameDisabled: boolean;
  isDeleteDisabled: boolean;
  isSaveDisabled: boolean;
  hasIncompleteData: boolean;
  isGlobalFiltersLimitExceeded: boolean;
  onViewChange: (event: { value: CpsGridView }) => void;
  onCreatePrivateView: () => void;
  onRenameView: () => void;
  onDeleteView: () => void;
  onUpdateView: () => void;
}

const getSaveButtonTitle = (
  translate: UserViewsToolbarProps['translate'],
  hasIncompleteData: boolean,
  isGlobalFiltersLimitExceeded: boolean
): string | undefined => {
  if (hasIncompleteData) {
    return translate('custom.settings.views.incompleteData');
  }

  if (isGlobalFiltersLimitExceeded) {
    return translate('custom.settings.views.globalFiltersLimitExceeded');
  }

  return undefined;
};

export const UserViewsToolbar: React.FC<UserViewsToolbarProps> = ({
  views,
  selectedView,
  translate,
  isPrivateViewLimitReached,
  isRenameDisabled,
  isDeleteDisabled,
  isSaveDisabled,
  hasIncompleteData,
  isGlobalFiltersLimitExceeded,
  onViewChange,
  onCreatePrivateView,
  onRenameView,
  onDeleteView,
  onUpdateView
}) => {
  const sortedViews = getSortedViews(views);
  const saveButtonTitle = getSaveButtonTitle(translate, hasIncompleteData, isGlobalFiltersLimitExceeded);

  return (
    <div className="viewsManagementButtons">
      <div className="selectViewDropdown">
        <DropDownList
          data={sortedViews}
          textField="name"
          dataItemKey="id"
          value={findViewById(views, selectedView) ?? null}
          onChange={onViewChange}
          fillMode={'solid'}
          size={'medium'}
          itemRender={renderViewDropdownItem}
        />
      </div>
      <div className="viewActionButtons">
        <span title={isPrivateViewLimitReached ? translate('custom.settings.views.maximumViewsReached') : undefined}>
          <Button
            onClick={onCreatePrivateView}
            size={'medium'}
            themeColor={'primary'}
            fillMode={'solid'}
            disabled={isPrivateViewLimitReached}
          >
            {translate('custom.settings.views.buttons.createPrivate')}
          </Button>
        </span>
        <Button
          onClick={onRenameView}
          size={'medium'}
          themeColor={'light'}
          fillMode={'solid'}
          disabled={isRenameDisabled}
        >
          {translate('custom.settings.views.buttons.renameView')}
        </Button>
        <Button
          onClick={onDeleteView}
          size={'medium'}
          themeColor={'light'}
          fillMode={'solid'}
          disabled={isDeleteDisabled}
        >
          {translate('custom.settings.views.buttons.deleteView')}
        </Button>
        <span title={saveButtonTitle}>
          <Button
            onClick={onUpdateView}
            size={'medium'}
            themeColor={'primary'}
            fillMode={'solid'}
            disabled={isSaveDisabled}
          >
            {translate('custom.common.save')}
          </Button>
        </span>
      </div>
    </div>
  );
};
